import { FormEvent, useCallback, useContext, useState } from "react";

import { Plus } from "@magnetic/icons";
import { Icon, Nav } from "@magnetic/nav";

import { Button } from "src/components/buttons";
import { Search } from "src/components/search";
import { UserRoleType } from "src/dataTypes/userRoleType";
import { UserContext } from "src/gen/schema/fred/fapi";
import { UserRole } from "src/gen/schema/models/auth";
import { dispatchC3TenantSwitchEvent } from "src/utils/c3Utils";
import { RegionFlag } from "src/utils/region/RegionFlag";

import { LoginContext } from "./loginContext";

import "./navbar.scss";
import "./orgSwitcher.scss";

interface TenantProps {
  readonly context: UserContext;
}

/**
 * Used to map the UserContext to
 * key by Org ID. this way the tenants
 * can be grouped in the drawer by
 * org.
 */
const mapUserContextToOrgs = (
  contexts: UserContext[]
): Map<string, UserContext[]> => {
  return contexts.reduce((out, context: UserContext) => {
    const tmp = out.get(context.orgId);
    if (tmp) {
      return out.set(context.orgId, [...tmp, context]);
    } else {
      return out.set(context.orgId, [context]);
    }
  }, new Map<string, UserContext[]>());
};

const Tenants = ({ context }: TenantProps) => {
  const { session, switchTenant } = useContext(LoginContext);
  if (session) {
    const { tenantId, tenantName, role, geo, cuiTenantId } = context;
    const contextRole = new UserRoleType(
      role || UserRole.USER_ROLE_UNSPECIFIED
    );
    return (
      <Nav.Drawer.Item
        className="org-tenant"
        onClick={() => {
          if (session?.isC3Crosslaunch) {
            dispatchC3TenantSwitchEvent(cuiTenantId);
            return;
          }
          switchTenant(tenantId);
        }}
        selected={session?.tenantId === tenantId}
      >
        <div title={tenantName} className="org-tenant-name">
          {tenantName}
        </div>
        {window.cnc?.settings?.get("multiRegionOrgUI") && (
          <RegionFlag geo={geo} />
        )}
        {contextRole.render()}
      </Nav.Drawer.Item>
    );
  }
  return null;
};

const sortAlphabetically = (a: string = "", b: string = "") =>
  a.localeCompare(b, undefined, { sensitivity: "base" });

interface OrgsSwitcherDrawerProps {
  readonly openOrgModal: () => void;
}

export const OrgsSwitcherDrawer = ({
  openOrgModal
}: OrgsSwitcherDrawerProps) => {
  const { session, userContexts } = useContext(LoginContext);
  const [orgSearch, setOrgSearch] = useState<string>("");

  const handleSearchCriteriaChange = useCallback(
    (e: FormEvent<HTMLInputElement>): void => {
      const { value } = e.currentTarget;
      setOrgSearch(value);
    },
    []
  );

  if (userContexts?.contexts && session) {
    const contexts = mapUserContextToOrgs(userContexts.contexts);

    const items = Array.from(contexts.entries())
      .sort((a, b) =>
        sortAlphabetically(a?.[1]?.[0]?.orgName, b?.[1]?.[0]?.orgName)
      )
      .map(([key, context]) => {
        const filteredContext = context.filter((ctx) =>
          ctx.tenantName.toLowerCase().includes(orgSearch.toLowerCase())
        );

        if (filteredContext.length === 0 && orgSearch !== "") {
          return null;
        }

        return (
          <Nav.Drawer.ItemGroup className="org-drawer" key={key} label="">
            {filteredContext
              .sort((a, b) => sortAlphabetically(a?.tenantName, b?.tenantName))
              .map((c) => (
                <Tenants key={c.tenantId} context={c} />
              ))}
          </Nav.Drawer.ItemGroup>
        );
      });

    return (
      <Nav.Drawer.Content
        heading="Select an organization"
        id="organization"
        key="org"
      >
        <Search
          label=""
          className="org-search-bar"
          onChange={handleSearchCriteriaChange}
          options={[]}
          placeholder="Search organization name"
        />
        {!session?.federatedUser && (
          <Button
            icon={<Plus size={14} weight="bold" />}
            name="create-org"
            className="org-create-button"
            kind="tertiary"
            onClick={openOrgModal}
            size="md"
          >
            Create new organization
          </Button>
        )}
        {items}
      </Nav.Drawer.Content>
    );
  }
  return null;
};

export const OrgSwitcher = () => {
  const { session, userContexts } = useContext(LoginContext);
  const currentContext = userContexts?.contexts?.find(
    (ctx) => ctx.tenantId === session?.tenantId
  );

  const switcherHeading = (
    <div className="org-switcher-heading">
      <span className="org-switcher-heading-type">Organization</span>
      <span className="org-switcher-heading-name">
        <span className="org-switcher-heading-name-text">
          {session?.tenantName}
        </span>
        {window.cnc?.settings?.get("multiRegionOrgUI") && (
          <RegionFlag geo={currentContext?.geo} showTooltip={false} />
        )}
      </span>
    </div>
  );

  return (
    <Nav.Item
      drawerContentId="organization"
      heading={switcherHeading}
      icon={<Icon kind="organization-switcher" />}
      label={session?.tenantName ?? "Organization"}
      switcher={true}
    />
  );
};
